import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Crosshair, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Onboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    product_service: "",
    target_audience: "",
    city: "",
    instagram_url: "",
    website_url: "",
    avg_price: "",
    goal: ""
  });

  const totalSteps = 7;
  const progress = (step / totalSteps) * 100;

  const handleNext = () => {
    if (step < totalSteps) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      // Criar cliente
      const clientRes = await axios.post(`${API}/clients`, {
        name: formData.name,
        email: formData.email
      });
      
      const clientId = clientRes.data.id;
      
      // Criar marca
      const brandRes = await axios.post(`${API}/brands`, {
        client_id: clientId,
        product_service: formData.product_service,
        target_audience: formData.target_audience,
        city: formData.city,
        instagram_url: formData.instagram_url,
        website_url: formData.website_url,
        avg_price: formData.avg_price,
        goal: formData.goal
      });
      
      const brandId = brandRes.data.id;
      
      toast.success("Cadastro concluído! Iniciando análise...");
      
      // Redirecionar para análise
      navigate(`/brand/${brandId}`);
      
    } catch (error) {
      console.error("Erro ao cadastrar:", error);
      toast.error("Erro ao processar cadastro. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-red-900/10 via-transparent to-transparent"></div>
      
      <div className="relative z-10 container mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white"
            data-testid="btn-back-home"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div className="flex items-center space-x-3">
            <Crosshair className="w-8 h-8 text-red-600" />
            <span className="text-2xl font-bold">MÍDIA TOMAHAWK</span>
          </div>
          <div className="w-20"></div>
        </div>

        {/* Progress */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400">Passo {step} de {totalSteps}</span>
            <span className="text-sm text-gray-400">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Form */}
        <div className="max-w-2xl mx-auto bg-[#1a1a1a] border border-gray-800 rounded-lg p-8">
          {step === 1 && (
            <div className="space-y-6" data-testid="step-1">
              <div>
                <h2 className="text-3xl font-bold mb-2">Bem-vindo!</h2>
                <p className="text-gray-400">Vamos começar com suas informações básicas</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Seu Nome</Label>
                  <Input
                    id="name"
                    data-testid="input-name"
                    placeholder="Digite seu nome"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="mt-2"
                  />
                </div>
                
                <div>
                  <Label htmlFor="email">Seu E-mail</Label>
                  <Input
                    id="email"
                    data-testid="input-email"
                    type="email"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="mt-2"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6" data-testid="step-2">
              <div>
                <h2 className="text-3xl font-bold mb-2">Qual seu produto/serviço?</h2>
                <p className="text-gray-400">Descreva o que você oferece</p>
              </div>
              
              <div>
                <Textarea
                  data-testid="input-product"
                  placeholder="Ex: Salão de beleza especializado em cabelos cacheados"
                  value={formData.product_service}
                  onChange={(e) => setFormData({...formData, product_service: e.target.value})}
                  className="min-h-[120px]"
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6" data-testid="step-3">
              <div>
                <h2 className="text-3xl font-bold mb-2">Quem é seu público?</h2>
                <p className="text-gray-400">Descreva quem você acredita ser seu cliente ideal</p>
              </div>
              
              <div>
                <Textarea
                  data-testid="input-audience"
                  placeholder="Ex: Mulheres de 25 a 45 anos, classe B, que buscam cuidados capilares"
                  value={formData.target_audience}
                  onChange={(e) => setFormData({...formData, target_audience: e.target.value})}
                  className="min-h-[120px]"
                />
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6" data-testid="step-4">
              <div>
                <h2 className="text-3xl font-bold mb-2">Onde você atua?</h2>
                <p className="text-gray-400">Em qual cidade seu negócio opera</p>
              </div>
              
              <div>
                <Input
                  data-testid="input-city"
                  placeholder="Ex: São Paulo"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                />
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-6" data-testid="step-5">
              <div>
                <h2 className="text-3xl font-bold mb-2">Suas redes sociais</h2>
                <p className="text-gray-400">Cole o link do Instagram e site (opcional)</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="instagram">Instagram</Label>
                  <Input
                    id="instagram"
                    data-testid="input-instagram"
                    placeholder="https://instagram.com/seu_perfil"
                    value={formData.instagram_url}
                    onChange={(e) => setFormData({...formData, instagram_url: e.target.value})}
                    className="mt-2"
                  />
                </div>
                
                <div>
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    data-testid="input-website"
                    placeholder="https://seusite.com"
                    value={formData.website_url}
                    onChange={(e) => setFormData({...formData, website_url: e.target.value})}
                    className="mt-2"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-6" data-testid="step-6">
              <div>
                <h2 className="text-3xl font-bold mb-2">Preço médio</h2>
                <p className="text-gray-400">Quanto custa em média seu produto/serviço?</p>
              </div>
              
              <div>
                <Input
                  data-testid="input-price"
                  placeholder="Ex: R$ 150,00"
                  value={formData.avg_price}
                  onChange={(e) => setFormData({...formData, avg_price: e.target.value})}
                />
              </div>
            </div>
          )}

          {step === 7 && (
            <div className="space-y-6" data-testid="step-7">
              <div>
                <h2 className="text-3xl font-bold mb-2">Qual seu objetivo?</h2>
                <p className="text-gray-400">O que você deseja alcançar com tráfego pago?</p>
              </div>
              
              <div>
                <Select
                  value={formData.goal}
                  onValueChange={(value) => setFormData({...formData, goal: value})}
                >
                  <SelectTrigger data-testid="select-goal">
                    <SelectValue placeholder="Selecione seu objetivo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vendas">Aumentar Vendas</SelectItem>
                    <SelectItem value="reservas">Gerar Reservas/Agendamentos</SelectItem>
                    <SelectItem value="visitas">Aumentar Visitas na Loja</SelectItem>
                    <SelectItem value="leads">Captar Leads</SelectItem>
                    <SelectItem value="trafego_local">Tráfego Local</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-800">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={step === 1}
              className="border-gray-700 hover:bg-gray-800"
              data-testid="btn-back"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            
            {step < totalSteps ? (
              <Button
                onClick={handleNext}
                className="bg-red-600 hover:bg-red-700"
                data-testid="btn-next"
              >
                Próximo
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700"
                data-testid="btn-submit"
              >
                {loading ? "Processando..." : "Finalizar"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
